import { computed, DEFAULT_CURRENCY_CODE, effect, inject, Injectable, signal } from '@angular/core';
import { PunchoutCart } from '@idealsupply/ngclient-webservice-cxml';
import { CartComponentStore } from 'cart-data';
import { ItemPriceUtil, ProductDetailsStore, stringifyItemId } from 'products-data';
import { ViewSignal } from 'state-data';

@Injectable()
export class PunchoutCartComponentStore extends CartComponentStore {
  private readonly _defaultCurrency = inject(DEFAULT_CURRENCY_CODE);
  private readonly _productDetailsStore = inject(ProductDetailsStore);
  private readonly _productDetailSignals = signal<
    ViewSignal<ReturnType<ProductDetailsStore['getProductDetails']> extends ViewSignal<infer T> ? T : never>[]
  >([]);
  private readonly _productDetails = computed(() => this._productDetailSignals().map((sig) => sig()));

  public readonly punchoutCartLoading = computed(() => {
    return this.cartLoading() || this.cartPricesLoading() || this._productDetailSignals().some((sig) => sig.loading());
  });

  public readonly punchoutCartErrors = computed(() => {
    const errors = [];
    if (this.cartError()) {
      errors.push(this.cartError());
    }
    if (this.cartPricesErrors()) {
      errors.push(this.cartPricesErrors());
    }
    this._productDetailSignals().forEach((sig) => {
      if (sig.error()) {
        errors.push(sig.error());
      }
    });
    return errors.length ? errors : null;
  });

  public readonly punchoutCartReady = computed(() => {
    return !this.punchoutCartLoading() && !this.punchoutCartErrors();
  });

  public readonly punchoutCart = computed<PunchoutCart>(() => {
    return {
      id: this.cartNumericId() ?? 0,
      currency: this._defaultCurrency,
      // Fees excluded, as on main: cxml adds EHC itself for EHCINPRICE customers (and recomputes the
      // total for them), so fees here would double-count it.
      subtotal: this.cartSubTotal(),
      items: this.cartPrices().map((item) => {
        // Product details are keyed by product, so compare on the bare product id — an ItemId with
        // wire options would otherwise never match its own product.
        const detail = this._productDetails().find((detail) => item.itemId.toProductId().equals(detail?.id));
        return {
          // Spec item id ({lineCode}{partNumber}[|WIRE|opt], no metadata) — cxml v1 splits it into
          // SupplierPartID / SupplierPartAuxiliaryID (id-format.md §8).
          id: stringifyItemId(item.itemId),
          description: detail?.description ?? '',
          quantity: item.pricing.quantity,
          // Price for one sold-in unit (pack / reel / cut length), without fees — cart pricing is the
          // extended line total, and cxml adds EHC itself for EHCINPRICE customers.
          unitPrice: ItemPriceUtil.calculatePrice(1, item.pricing.pricingModel).price,
          unit: detail?.unit ?? 'ea',
          classification: detail?.unspsc ?? '',
        };
      }),
    };
  });

  constructor() {
    super();
    effect(() => {
      const signals = this.cartItems().map((item) => this._productDetailsStore.getProductDetails(item.id));
      this._productDetailSignals.set(signals);
    });
  }
}
